import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import { Input, Modal, Table, TableColumnType } from 'antd';
import { Button, Message } from '@alifd/next';
import client from '@/lib/feathers';
import { RepairSubject } from '@/types/database/repair';
import { ColorPicker, Color, createColor } from 'material-ui-color';
import Swal from 'sweetalert2';
import { Footer, ColorBlock, EditContainer, ColorContainer } from './styles';

const repairSubjectsService = client.service('repairSubjects');

const EditRepairSubjectButtonWithModal: React.FC<{
  backStageUpdateTrigger: Record<string, never>;
  setBackStageUpdateTrigger: Dispatch<SetStateAction<Record<string, never>>>;
}> = ({ backStageUpdateTrigger, setBackStageUpdateTrigger }) => {
  const [visible, setVisible] = useState(false);
  const [subjects, setSubjects] = useState<RepairSubject[]>([]);
  const [settingIndex, setSettingIndex] = useState<number | null>(null);
  const [currentSetting, setCurrentSetting] = useState<RepairSubject | null>(
    null
  );
  const [pickedColor, setPickedColor] = useState<Color>(createColor('#fff'));

  const handleCreateSubject = useCallback(async () => {
    const getHexColor = () => {
      const value = Array(4)
        .fill(0)
        .map(() =>
          Math.floor(Math.random() * 255)
            .toString(16)
            .padStart(2, '0')
        )
        .join('');
      return `#${value}`;
    };
    try {
      await repairSubjectsService.create({
        name: '未命名項目',
        color: getHexColor(),
      });
      setBackStageUpdateTrigger({});
      Message.success('新增成功');
      setCurrentSetting(null);
      setSettingIndex(null);
    } catch (err) {
      console.error(err);
    }
  }, [setBackStageUpdateTrigger]);

  const handleUpdateSubject = useCallback(
    async (id: string) => {
      try {
        await repairSubjectsService.patch(id, currentSetting);
        setBackStageUpdateTrigger({});
        setCurrentSetting(null);
        setSettingIndex(null);
        Message.success('更新成功');
      } catch (err) {
        console.error(err);
      }
    },
    [currentSetting, setBackStageUpdateTrigger]
  );

  const handleCancelUpdateSubject = useCallback(() => {
    setCurrentSetting(null);
    setSettingIndex(null);
  }, []);

  const handleDeleteSubject = useCallback(
    async (id: string) => {
      const result = await Swal.fire({
        title: '確定要刪除嗎？',
        text: '刪除後將無法復原',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      });
      if (result.value) {
        try {
          await repairSubjectsService.remove(id);
          setBackStageUpdateTrigger({});
          Message.success('刪除成功');
        } catch (err) {
          console.error(err);
        }
      }
    },
    [setBackStageUpdateTrigger]
  );

  const columns: TableColumnType<RepairSubject>[] = useMemo(
    () => [
      {
        title: '維修項目名稱',
        dataIndex: 'name',
        width: '40%',

        render: (name: string, _: any, index: number) => {
          if (index === settingIndex) {
            return (
              <Input
                value={currentSetting?.name}
                onChange={(e) => {
                  setCurrentSetting((prev) => {
                    if (prev) {
                      return {
                        ...prev,
                        name: e.target.value,
                      };
                    } else {
                      return prev;
                    }
                  });
                }}
              />
            );
          } else {
            return name;
          }
        },
      },
      {
        title: '顏色',
        dataIndex: 'color',
        width: '20%',
        render: (color: string, _: any, index: number) => {
          if (index === settingIndex) {
            return (
              <ColorContainer>
                <ColorPicker
                  hideTextfield
                  value={pickedColor}
                  onChange={(c) => {
                    setPickedColor(c);
                    setCurrentSetting((prev) => {
                      if (prev) {
                        return {
                          ...prev,
                          color: `#${c.hex.toLowerCase()}`,
                        };
                      } else {
                        return prev;
                      }
                    });
                  }}
                />
              </ColorContainer>
            );
          } else {
            return (
              <ColorContainer>
                <ColorBlock color={color} />
              </ColorContainer>
            );
          }
        },
      },
      {
        title: '操作',
        width: '40%',
        dataIndex: '_id',
        render: (id: string, subject: RepairSubject, index: number) => {
          return (
            <EditContainer>
              {settingIndex === null && (
                <>
                  <Button
                    onClick={() => {
                      setCurrentSetting(subject);
                      setSettingIndex(index);
                      setPickedColor(createColor(subject.color));
                    }}
                  >
                    編輯
                  </Button>
                  <Button
                    type="normal"
                    warning
                    onClick={() => handleDeleteSubject(id)}
                  >
                    刪除
                  </Button>
                </>
              )}
              {settingIndex === index && (
                <>
                  <Button
                    type="primary"
                    onClick={() => handleUpdateSubject(id)}
                  >
                    確定
                  </Button>
                  <Button onClick={handleCancelUpdateSubject}>取消</Button>
                </>
              )}
            </EditContainer>
          );
        },
      },
    ],
    [
      currentSetting,
      handleCancelUpdateSubject,
      handleDeleteSubject,
      handleUpdateSubject,
      pickedColor,
      settingIndex,
    ]
  );

  useEffect(() => {
    const getSubjects = async () => {
      try {
        const res = await repairSubjectsService.find({
          query: { $sort: { _id: -1 } },
        });
        if (res.data) {
          setSubjects(res.data);
        }
      } catch (err) {
        console.error(err);
      }
    };
    getSubjects();
  }, [visible, backStageUpdateTrigger]);

  return (
    <>
      <Button
        style={{
          marginLeft: '10px',
        }}
        onClick={() => setVisible(true)}
      >
        編輯修繕項目
      </Button>
      <Modal
        title="編輯修繕項目"
        visible={visible}
        onOk={() => {
          setVisible(false);
          setSettingIndex(null);
        }}
        okText="確定"
        onCancel={() => {
          setVisible(false);
          setSettingIndex(null);
        }}
        cancelButtonProps={{ style: { display: 'none' } }}
        bodyStyle={{
          padding: '0px',
        }}
        footer={null}
      >
        <Table
          dataSource={subjects}
          columns={columns}
          pagination={false}
          scroll={{
            y: 600,
            scrollToFirstRowOnChange: true,
          }}
        />
        <Footer>
          <Button onClick={handleCreateSubject}>新增</Button>
        </Footer>
      </Modal>
    </>
  );
};

export default EditRepairSubjectButtonWithModal;
